import React, { useEffect, useState, useRef } from 'react';

/**
 * WaveformVisualizer - Animated sine-wave bar chart used as a microphone level indicator.
 * When `isActive` is true, bars animate continuously using a sinusoidal pattern.
 * When inactive, all bars collapse to their minimum height.
 *
 * @param {Object} props
 * @param {boolean} [props.isActive=false] - Whether the waveform animation is running.
 * @param {number} [props.barCount=4] - Number of bars to display.
 * @returns {JSX.Element}
 */
const WaveformVisualizer = ({ isActive = false, barCount = 4 }) => {
  const [barHeights, setBarHeights] = useState(Array(barCount).fill(6));
  const phaseRef = useRef(0);

  useEffect(() => {
    if (!isActive) {
      setBarHeights(Array(barCount).fill(6));
      return;
    }

    // Gentle wave animation using sine waves
    const updateBars = () => {
      phaseRef.current += 0.15;
      
      setBarHeights(Array(barCount).fill(0).map((_, i) => {
        const minHeight = 6;
        const maxHeight = 14;
        
        // Gentle sine wave with offset per bar
        const wave = Math.sin(phaseRef.current + i * 0.8) * 0.5 + 0.5;
        const height = minHeight + wave * (maxHeight - minHeight);
        
        return height;
      }));
    };

    // Slower update for chill vibes
    const interval = setInterval(updateBars, 150);
    updateBars();
    
    return () => clearInterval(interval);
  }, [isActive, barCount]);

  return (
    <div 
      className="flex items-center gap-1 h-5"
      role="img"
      aria-label={isActive ? "Recording audio" : "Audio inactive"}
    >
      {barHeights.map((height, index) => (
        <div
          key={index}
          className="w-0.5 rounded-full bg-blue-400 transition-all duration-200 ease-in-out"
          style={{
            height: `${height}px`,
          }}
        />
      ))}
    </div>
  );
};

export default WaveformVisualizer;

